/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Component } from '@appCanvasCommon/component-system/Component';
import { EventBinder } from '../interaction-utils';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { PlanAccessLevel, Ticket } from '@touchplan/common';
import { CONTEXT_MENUS, ContextMenuService } from 'src/app/modules/ticket-edit/context-menu.service';
import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { Point } from 'pixi.js-legacy';
import { Camera } from '@appCanvasCommon/core/Camera';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';

export class TicketQuickStatus extends Component implements ComponentInterface {
	private _binder: EventBinder;
	private _ticketContainer: TicketContainer;
	private _isDown = false;
	constructor() {
		super();
	}
	public override setup(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer) {
		this.canvasAngularLand = canvasAngularLand;
		this._ticketContainer = dataContainer;
		this._binder = new EventBinder(this.getParent());

		// Two Finger Tap for tablet devices
		this._binder.add('hammer-singletap2', () => {
			this._isDown = true;
			this.handleAction();
		});

		// Right-click for desktop devices
		this._binder.add('rightdown', () => {
			this._isDown = true;
			this.handleAction();
		});

		// Ticket hold
		this._binder.add('hammer-pressup', () => {
			if (this._isDown && canvasAngularLand.tickets.editTicket$.getValue().size <= 0) {
				this.handleAction();
			}
			this._isDown = false;
		});
	}

	private handleAction(): void {
		const editTickets = this.canvasAngularLand.tickets.editTicket$.getValue();
		if (
			editTickets.size <= 0 &&
			this.canOpenContextMenu(editTickets, [PlanAccessLevel.live, PlanAccessLevel.locked])
		) {
			this.handleQuickStatusMenu();
		} else if (editTickets.size === 1 && this.canOpenContextMenu(editTickets, [PlanAccessLevel.live])) {
			this.handleTicketEditConfirmContextMenu();
			// } else if (editTickets.size > 1) {
			// Neither menu is supported for multi-edit
		}
	}

	private canOpenContextMenu(
		tickets: Map<string, Ticket>,
		allowedPlanAccessLevels: PlanAccessLevel[]
	): boolean {
		let canOpen = true;
		const clickedTicket: Ticket = this._ticketContainer.getData();

		//check 1: are you clicking a ticket that is being edited
		tickets.forEach((ticket) => {
			if (canOpen && ticket.$id === clickedTicket.$id) {
				canOpen = false;
			}
		});
		//check 2: does the plan has right access level ? Using the legacy accessCheck service since everything canvas is
		if (
			canOpen &&
			this.canvasAngularLand.planAccessCheck.accessLevelSync &&
			!allowedPlanAccessLevels.includes(this.canvasAngularLand.planAccessCheck.accessLevelSync)
		) {
			canOpen = false;
		}

		return canOpen;
	}

	private handleTicketEditConfirmContextMenu(): void {
		const ticket: Ticket = this._ticketContainer.getData();
		const menuService: ContextMenuService = this.canvasAngularLand.contextMenuService;
		menuService.show(CONTEXT_MENUS.TICKET_EDIT_CONFIRM_MENU, this.getContextMenuDrawPosition(ticket), {
			tickets: ticket,
		});
	}

	private getContextMenuDrawPosition(ticket: Ticket): Point {
		const camera: Camera = this._ticketContainer.camera;
		// This section makes the context menu "stick" to the screen if you click a ticket where the right edge is not in the viewport
		const displayXOffset = 50; // 50 pixels from right edge of ticket
		const contextMenuWidth = 210;
		const displayWorldX: number = ticket.view.left + ticket.view.width + displayXOffset;
		const displayScreenX: number = camera.realToScreenX(displayWorldX) + contextMenuWidth;
		return new Point(
			displayScreenX >= camera.view.width
				? camera.screenToRealX(camera.view.width - contextMenuWidth - displayXOffset)
				: displayWorldX,
			ticket.view.top
		);
	}

	public handleQuickStatusMenu(): void {
		//lazy temp fix, use the tapped ticket
		const ticket: Ticket = this._ticketContainer.getData();
		let replaceSelection = false;

		let tickets = this.canvasAngularLand.tickets.selectedTicket$.getValue().list;
		//if the tapped ticket isn't in the list, replace the list with it
		if (!tickets.has(ticket.$id)) {
			replaceSelection = true;
			tickets = new Map();
			tickets.set(ticket.$id, ticket);
		}

		const valid = this.canvasAngularLand.interactionBlocker.check(Interaction.OpenTicketContextMenu, {
			tickets,
		});
		if (!valid) {
			return;
		}

		if (replaceSelection) {
			this.canvasAngularLand.actions.unSelectTicket(
				this.canvasAngularLand.tickets.selectedTicket$.getValue().list
			);
			this.canvasAngularLand.actions.selectTicket(ticket);
			this.canvasAngularLand.legacyActions.clearFloatingTickets();
		}

		const menuService: ContextMenuService = this.canvasAngularLand.contextMenuService;

		//TODO, get a rectangle of all tickets to determine "to the right of all tickets"

		menuService.show(CONTEXT_MENUS.TICKET_QUICK_STATUS, this.getContextMenuDrawPosition(ticket), { tickets });

		analyticsEventsService.clickFastUpdate(this.canvasAngularLand.planId, ticket.$id);
	}

	public override destroy() {
		this._binder.destroy();
		this._binder = null;
		this.canvasAngularLand = null;
		this._ticketContainer = null;
		super.destroy();
	}
}
